import { getSupabase } from '@/lib/supabase';

let lastToken: string | null = null;
let lastFetchedAt = 0;
const TOKEN_TTL_MS = 60_000; // 60s soft memo to avoid waterfalls on mount

/**
 * Clear the cached token. Useful when logging in/out or when session changes.
 */
export function clearTokenCache(): void {
  lastToken = null;
  lastFetchedAt = 0;
}

export async function getAccessToken(): Promise<string | null> {
  const now = Date.now();
  // Only use cached token if it's non-null and within TTL
  // If cached token is null, always check for fresh session (session might have been restored)
  if (lastToken !== null && now - lastFetchedAt < TOKEN_TTL_MS) {
    return lastToken;
  }
  try {
    const sb = getSupabase();
    const { data, error } = await sb.auth.getSession();
    
    if (error) {
      console.warn('[getAccessToken] Supabase session error:', error);
      // Try to refresh the session if there's an error
      try {
        const { data: refreshData, error: refreshError } = await sb.auth.refreshSession();
        if (!refreshError && refreshData.session) {
          lastToken = refreshData.session.access_token ?? null;
          lastFetchedAt = now;
          return lastToken;
        }
      } catch (refreshErr) {
        console.warn('[getAccessToken] Failed to refresh session:', refreshErr);
      }
      lastToken = null;
      lastFetchedAt = now;
      return null;
    }
    
    const token = data.session?.access_token ?? null;
    if (!token) {
      console.warn('[getAccessToken] No session or access token found. Session exists:', !!data.session, 'User:', data.session?.user?.id);
      // Check if session expired
      if (data.session?.expires_at) {
        const expiresAt = data.session.expires_at * 1000; // Convert to milliseconds
        if (now >= expiresAt) {
          console.warn('[getAccessToken] Session expired. Expires at:', new Date(expiresAt).toISOString(), 'Current time:', new Date(now).toISOString());
          // Try to refresh
          try {
            const { data: refreshData, error: refreshError } = await sb.auth.refreshSession();
            if (!refreshError && refreshData.session) {
              lastToken = refreshData.session.access_token ?? null;
              lastFetchedAt = now;
              return lastToken;
            }
          } catch (refreshErr) {
            console.warn('[getAccessToken] Failed to refresh expired session:', refreshErr);
          }
        }
      }
    }
    
    lastToken = token;
    lastFetchedAt = now;
    return token;
  } catch (e) {
    console.error('[getAccessToken] Exception getting session:', e);
    lastToken = null;
    lastFetchedAt = now;
    return null;
  }
}

